import { GameSceneData } from '../scenes/GameScene';

const AUTOSAVE_KEY = 'bombastik:autosave:v1';
const SLOT_PREFIX = 'bombastik:slot:v1:';
const NAMES_KEY = 'bombastik:names:v1';
const HIGH_SCORE_KEY = 'bombastik:highscore:v1';

export interface SaveBlob {
  version: 1;
  savedAt: number;
  data: GameSceneData;
}

export function relativeTime(savedAt: number): string {
  const diff = Date.now() - savedAt;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'acum';
  if (min < 60) return `acum ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `acum ${h}h`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'ieri' : `acum ${d}z`;
}

// Handles version migration — returns null for corrupt/unknown blobs.
function migrateBlob(raw: unknown): SaveBlob | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r['version'] === 1 && r['data'] && typeof r['data'] === 'object') {
    return {
      version: 1,
      savedAt: typeof r['savedAt'] === 'number' ? r['savedAt'] : 0,
      data: r['data'] as GameSceneData,
    };
  }
  return null;
}

export function saveAutosave(data: GameSceneData) {
  try {
    const blob: SaveBlob = { version: 1, savedAt: Date.now(), data };
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(blob));
  } catch {
    // localStorage may be unavailable (private mode, quota); silent for MVP
  }
}

export function loadAutosave(): SaveBlob | null {
  try {
    const raw = localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return null;
    return migrateBlob(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearAutosave() {
  try { localStorage.removeItem(AUTOSAVE_KEY); } catch {}
}

export function saveSlot(slot: 1 | 2 | 3, data: GameSceneData) {
  try {
    const blob: SaveBlob = { version: 1, savedAt: Date.now(), data };
    localStorage.setItem(SLOT_PREFIX + slot, JSON.stringify(blob));
  } catch {}
}

export function loadSlot(slot: 1 | 2 | 3): SaveBlob | null {
  try {
    const raw = localStorage.getItem(SLOT_PREFIX + slot);
    if (!raw) return null;
    return migrateBlob(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function deleteSlot(slot: 1 | 2 | 3) {
  try { localStorage.removeItem(SLOT_PREFIX + slot); } catch {}
}

export interface NamesProfile {
  p1: string;
  p2: string;
}

export function loadNames(): NamesProfile {
  try {
    const raw = localStorage.getItem(NAMES_KEY);
    if (!raw) return { p1: 'GEO', p2: 'ARIS' };
    const parsed = JSON.parse(raw);
    return {
      p1: (parsed.p1 ?? 'GEO').toString().slice(0, 8).toUpperCase(),
      p2: (parsed.p2 ?? 'ARIS').toString().slice(0, 8).toUpperCase(),
    };
  } catch {
    return { p1: 'GEO', p2: 'ARIS' };
  }
}

export function saveNames(names: NamesProfile) {
  try {
    localStorage.setItem(NAMES_KEY, JSON.stringify({
      p1: names.p1.slice(0, 8).toUpperCase(),
      p2: names.p2.slice(0, 8).toUpperCase(),
    }));
  } catch {}
}

export function loadHighScore(): number {
  try {
    return parseInt(localStorage.getItem(HIGH_SCORE_KEY) ?? '0', 10) || 0;
  } catch {
    return 0;
  }
}

export function saveHighScore(score: number) {
  try {
    if (score > loadHighScore()) {
      localStorage.setItem(HIGH_SCORE_KEY, String(score));
    }
  } catch {}
}
